// src/contexts/ClientContext.jsx
// ============================================================
// CLIENT CONTEXT: Provides client list and current client state
// 
// CLIENT ID SOURCE OF TRUTH: AuthContext session.client_id
// This context syncs with AuthContext - it does NOT set defaults.
// ============================================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

import { API_BASE, apiFetch } from '../config/apiBase';
const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const { session, sessionLoading } = useAuth();
  
  // ============================================================
  // CLIENT ID: Synced from AuthContext session (single source of truth)
  // No hardcoded default - null until session loads
  // ============================================================
  const [clientId, setClientId] = useState(session?.client_id || null);
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Sync clientId with session.client_id from AuthContext
  useEffect(() => {
    if (session?.client_id) {
      console.log('🏥 ClientContext: Syncing clientId from session:', session.client_id);
      setClientId(session.client_id);
    }
  }, [session?.client_id]);

  // Fetch clients from API
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await apiFetch(`${API_BASE}/api/v2/clients`);
        if (!response.ok) throw new Error('Failed to fetch clients');
        
        const data = await response.json();
        const clientList = data.data || [];
        
        // Ensure all client IDs are strings for BigQuery compatibility
        const normalizedClients = clientList.map(client => ({
          ...client,
          id: String(client.id)
        }));
        
        setClients(normalizedClients);
        setUsingFallback(false);
        setLoading(false);
      } catch (err) {
        console.error('❌ ClientContext: Error fetching clients:', err);
        setError(err.message);
        setLoading(false);

        // ============================================================
        // FALLBACK CLIENTS: Display-only when API fails
        // These are NOT used to set default clientId - just for UI display
        // (e.g., client dropdown, admin tools)
        // ============================================================
        console.warn('⚠️ ClientContext: Using fallback client list for display');
        const fallbackClients = [
          { id: '1', name: 'Baseline Analytics' },
          { id: '101', name: 'Sample Health System' },
          { id: '200', name: 'Regional Health System' },
          { id: '300', name: 'North Star Consortium', isMedicalTourism: true }
        ];
        setClients(fallbackClients);
        setUsingFallback(true);
      }
    };
    
    fetchClients();
  }, []);

  /**
   * Update the current client ID
   * Note: This is for admin/dev override functionality.
   * Normal users should have clientId set via AuthContext session.
   */
  const updateClient = (newClientId) => {
    const stringClientId = String(newClientId); // Ensure string for BigQuery
    console.log('🏥 ClientContext: Updating clientId to:', stringClientId);
    setClientId(stringClientId);
    window.dispatchEvent(new CustomEvent('clientChanged', { detail: { clientId: stringClientId } }));
  };

  // Find current client from list (handles both string and number comparisons)
  const currentClient = clients.find(c => String(c.id) === String(clientId));

  return (
    <ClientContext.Provider value={{ 
      clientId, 
      clients, 
      currentClient,
      updateClient,
      loading: loading || sessionLoading, // Include session loading state
      error,
      usingFallback // Expose whether we're using fallback data
    }}>
      {children}
    </ClientContext.Provider>
  );
};